import { useId, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { apiPost } from '../../api/client.js';
import { useToast } from '../../hooks/useToast.js';

export interface CreateUserResult {
  id: string;
  username: string;
  height_cm: number;
  must_reset_password: 0 | 1;
}

interface FieldErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
  height_cm?: string;
}

export interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (user: CreateUserResult) => void;
}

const DEFAULT_HEIGHT = 175;

export function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: CreateUserDialogProps): ReactElement {
  const { push } = useToast();
  const usernameId = useId();
  const passwordId = useId();
  const confirmId = useId();
  const heightId = useId();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [heightCm, setHeightCm] = useState<string>(String(DEFAULT_HEIGHT));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  function reset(): void {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setHeightCm(String(DEFAULT_HEIGHT));
    setErrors({});
    setSubmitting(false);
  }

  function close(): void {
    reset();
    onClose();
  }

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    const u = username.trim();
    if (u.length < 3 || u.length > 64) {
      next.username = 'Username must be 3–64 characters';
    }
    if (password.length < 8) {
      next.password = 'Password must be at least 8 characters';
    }
    if (password !== confirmPassword) {
      next.confirmPassword = 'Passwords do not match';
    }
    const h = Number(heightCm);
    if (!Number.isFinite(h) || !Number.isInteger(h) || h < 50 || h > 250) {
      next.height_cm = 'Height must be between 50 and 250 cm';
    }
    return next;
  }

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setSubmitting(true);
    try {
      const created = await apiPost<CreateUserResult>('/api/users', {
        username: username.trim(),
        password,
        height_cm: Number(heightCm),
      });
      push('success', `User "${created.username}" created`);
      onCreated(created);
      close();
    } catch (err) {
      const apiErr = err as { code?: string; message?: string };
      if (apiErr?.code === 'USERNAME_TAKEN') {
        setErrors({ username: apiErr.message ?? 'Username taken' });
      } else {
        push('error', apiErr?.message ?? 'Failed to create user');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} title="Create New User">
      <form onSubmit={onSubmit} className="flex flex-col gap-3" data-testid="create-user-form">
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={usernameId} style={{ color: 'var(--text-muted)' }}>Username</label>
          <input
            id={usernameId}
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={{
              background: 'var(--bg-primary)',
              borderColor: errors.username ? '#dc2626' : 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.username ? (
            <span className="text-xs" style={{ color: '#dc2626' }} data-testid="error-username">
              {errors.username}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={passwordId} style={{ color: 'var(--text-muted)' }}>Password</label>
          <input
            id={passwordId}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={{
              background: 'var(--bg-primary)',
              borderColor: errors.password ? '#dc2626' : 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.password ? (
            <span className="text-xs" style={{ color: '#dc2626' }} data-testid="error-password">
              {errors.password}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={confirmId} style={{ color: 'var(--text-muted)' }}>Confirm password</label>
          <input
            id={confirmId}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={{
              background: 'var(--bg-primary)',
              borderColor: errors.confirmPassword ? '#dc2626' : 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.confirmPassword ? (
            <span className="text-xs" style={{ color: '#dc2626' }} data-testid="error-confirm">
              {errors.confirmPassword}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={heightId} style={{ color: 'var(--text-muted)' }}>Height (cm)</label>
          <input
            id={heightId}
            type="number"
            inputMode="numeric"
            min={50}
            max={250}
            step={1}
            value={heightCm}
            onChange={(e) => setHeightCm(e.target.value)}
            className="rounded-md border px-3 py-2 text-base outline-none"
            style={{
              background: 'var(--bg-primary)',
              borderColor: errors.height_cm ? '#dc2626' : 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.height_cm ? (
            <span className="text-xs" style={{ color: '#dc2626' }} data-testid="error-height">
              {errors.height_cm}
            </span>
          ) : null}
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="rounded-md border px-4 py-2 text-sm"
            style={{
              background: 'transparent',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              minHeight: 48,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md px-4 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              color: 'var(--accent-contrast)',
              minHeight: 48,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}